import { ArrowRight } from 'lucide-react';
import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { COURSES } from '@/content/training';
import { mailto } from '@/content/site';

/**
 * In-house training: three courses as three equal cards — the poster's scene,
 * who it is for, the title and subtitle, one line, three topics, and a link
 * that opens an email already titled with the course.
 */
export function TrainingSection() {
  return (
    <Plate eyebrow="Training" id="training" labelledBy="training-title">
      <h2 id="training-title" className="lp-h2 lp-reveal max-w-4xl" style={stagger(1)}>
        In-house training for your team.
      </h2>
      <p className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(2)}>
        Three courses we bring to your company: for engineers, for developers and for executives.
      </p>

      <ul className="mt-16 grid gap-5 md:mt-20 lg:grid-cols-3" aria-label="Courses">
        {COURSES.map((c, i) => (
          <li key={c.id} className="lp-reveal" style={stagger(3 + i)}>
            <article aria-labelledby={`course-${c.id}`} className="course-card">
              <img
                loading="lazy"
                decoding="async"
                src={`/img/${c.image}-960.webp`}
                srcSet={`/img/${c.image}-640.webp 640w, /img/${c.image}-960.webp 960w`}
                sizes="(min-width: 1360px) 410px, (min-width: 1024px) 30vw, (min-width: 768px) 90vw, 100vw"
                width={960}
                height={600}
                alt={c.alt}
                className="aspect-[16/10] w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-7 md:p-8">
                <p className="eyebrow">{c.audience}</p>
                <h3 id={`course-${c.id}`} className="t-h3 mt-4 text-2xl">
                  {c.title}
                </h3>
                <p className="mt-2 text-[15px] font-medium leading-snug text-foreground/85">{c.sub}</p>
                <p className="mt-5 text-[16px] leading-relaxed text-[hsl(var(--fg-secondary))]">{c.line}</p>
                {c.certs && (
                  <div className="mt-6 border-t border-border pt-6">
                    <div className="grid grid-cols-2 gap-4">
                      {c.certs.map((g) => (
                        <div key={g.level}>
                          <p className="eyebrow">{g.level}</p>
                          <ul className="mt-3 flex gap-2.5" aria-label={`${g.level} certifications`}>
                            {g.badges.map((b) => (
                              <li key={b.src}>
                                <img
                                  loading="lazy"
                                  decoding="async"
                                  src={b.src}
                                  alt={b.alt}
                                  width={170}
                                  height={176}
                                  className="h-[4.25rem] w-auto"
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <p className="t-caption mt-4">The exams and badges are NVIDIA’s; this course prepares you for them.</p>
                  </div>
                )}
                {c.logo && (
                  <div className="mt-6 border-t border-border pt-6">
                    <img
                      loading="lazy"
                      decoding="async"
                      src={c.logo.src}
                      alt={c.logo.alt}
                      width={c.logo.width}
                      height={c.logo.height}
                      className="h-16 w-auto"
                    />
                    {c.tools && (
                      <>
                        <p className="eyebrow mt-6">{c.tools.label}</p>
                        <ul className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3" aria-label={c.tools.label}>
                          {c.tools.marks.map((m) => (
                            <li key={m.src}>
                              <img
                                loading="lazy"
                                decoding="async"
                                src={m.src}
                                alt={m.alt}
                                width={m.width}
                                height={m.height}
                                className="h-6 w-auto"
                              />
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                <ul className="mt-6 space-y-2.5 border-t border-border pt-6">
                  {c.topics.map((t) => (
                    <li key={t} className="flex gap-3 text-[15px] leading-snug text-muted-foreground">
                      <span aria-hidden className="mt-[0.45em] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {t}
                    </li>
                  ))}
                </ul>
                <a href={mailto(`In-house training: ${c.title}`)} className="link-quiet mt-auto pt-8">
                  Ask about this course <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          </li>
        ))}
      </ul>
    </Plate>
  );
}
